import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedDialog,
	EnhancedSnackbar,
	IResponse,
	IServerError,
	SingleColumnLayout,
	StandardPanel,
} from "@websential/cosmic-v20";
import { FormProvider, useForm } from "react-hook-form";
import { Grid, useTheme } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";

import { BCEHTSettingInfoSetupFieldsPanel } from "./Panels/BCEHTSettingInfoSetupFieldsPanel";
import { BCEHTSettingService } from "../../services/BCEHTSettingService";
import { BCEHTSettingSetupToolbar } from "./BCEHTSettingSetupToolbar";
import { BCEHTSettingSetupValidationSchema } from "./validator";
import { IAppState } from "../../store/reducers";
import { IBCEHTSetting } from "../../models/BCEHTSetting";
import { IGetListRequest } from "../../models";
import { INITIAL_SNACKBAR_DATA } from "../../common/constants";
import { YearService } from "../../services/YearService";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../store/actions";
import { scrollPanelFieldsToTop, useNavigateToLogout } from "../../common/Common";
import { yupResolver } from "@hookform/resolvers/yup";

export interface IBCEHTSettingSetup { }
const SINGLE_RECORD_DATA_QUERY_KEY = "bc-eht-setting-single-data-record";
const BCEHTSetting_LIST_DATA_QUERY_KEY = "bc-eht-setting-list";

// const useGetData = (
// 	id: string | undefined,
// 	navigateToLogout: (path: string) => void
// ) =>
// 	useQuery({
// 		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, id, navigateToLogout],
// 		queryFn: () => {
// 			return BCEHTSettingService.getById(id, navigateToLogout);
// 		},
// 		refetchOnWindowFocus: false,
// 	});

const useYears = (
	params: IGetListRequest,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: ["years-list", params],
		queryFn: (data) => {
			return YearService.getList(params, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});
export const BCEHTSettingSetup: React.FC<IBCEHTSettingSetup> = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const subMenuVisibility = useSelector(
		(state: IAppState) => state.drawerAction.subMenuVisibility
	);
	const navigateToLogout = useNavigateToLogout();
	const [openDialog, setDialogOpen] = useState(false);
	const [openArchiveDialog, setArchiveDialogOpen] = useState(false);
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [bCEHTSettingId, setBCEHTSettingId] = useState<number | string>(0);
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const queryClient = useQueryClient();
	const { id } = useParams();
	const { taxYear } = useParams();
	const [isPending, setIsPending] = useState(false);
	const [editRecordId, setEditRecordId] = useState(taxYear);

	const { isPending: isSubmitting, mutate: submitBCEHTSettingFormFields } =
		useMutation({
			mutationFn: BCEHTSettingService.create,
			onSuccess: (res: IBCEHTSetting | IServerError | IResponse | any) => {
				if (res.status === 200) {
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					scrollPanelFieldsToTop();
					// let path = `/sbc-eht-setting/${res.data.id}`;
					// navigate(path);
					return Promise.all([
						queryClient.invalidateQueries({
							queryKey: [BCEHTSetting_LIST_DATA_QUERY_KEY],
						}),
						queryClient.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);
				} else if (res.status === 201) {
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// let path = `/sbc-eht-setting/${res.data.id}`;
					// navigate(path);
					scrollPanelFieldsToTop();
					return Promise.all([
						queryClient.invalidateQueries({
							queryKey: [BCEHTSetting_LIST_DATA_QUERY_KEY],
						}),
						queryClient.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);
				} else {
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong!",
						severity: "error",
					});
				}
			},
			onError: () => {
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	// const { isPending: isPendingById, data: selectedDataRecord } = useGetData(
	// 	editRecordId,
	// 	navigateToLogout
	// );

	const { isPending: isPendingYearsList, data: yearsDDLList } = useYears(
		{
			search_query: "",
			order_by: "client_name",
			order_direction: "asc",
			page: 1,
			size: 25,
		},
		navigateToLogout
	);

	const yearsDDLItems = useMemo(() => {
		if (yearsDDLList && Array.isArray(yearsDDLList && yearsDDLList.items)) {
			return (
				yearsDDLList &&
				yearsDDLList.items.map((year: any) => ({
					id: year.id,
					label: year.taxYear,
					value: year.taxYear,
				}))
			);
		}

		// Return empty array in case of API data fetching error
		return [];
	}, [yearsDDLList]);

	const DEFAULT_VALUES = {
		taxYear: undefined,
		exemptionAmount: null,
		notchAmount: null,
		rate1: null,
		rate2: null,
		installmentsFrom: null,
		charitableExcemptionAmount: null,
		charitableNotchAmount: null,
		charitableRate1: null,
		charitableRate2: null,
		charitableInstallmentsFrom: null,
	};
	const formMethods = useForm<any>({
		resolver: yupResolver(BCEHTSettingSetupValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: DEFAULT_VALUES,
	});

	const onSubmit = (values: any) => {
		let payload = {
			id: id && id,
			navigateToLogout: navigateToLogout,
			taxYear: values.taxYear,
			exemptionAmount: values.exemptionAmount,
			notchAmount: values.notchAmount,
			rate1: values.rate1,
			rate2: values.rate2,
			installmentsFrom: values.installmentsFrom,
			charitableExcemptionAmount: values.charitableExcemptionAmount,
			charitableNotchAmount: values.charitableNotchAmount,
			charitableRate1: values.charitableRate1,
			charitableRate2: values.charitableRate2,
			charitableInstallmentsFrom: values.charitableInstallmentsFrom,
		};
		submitBCEHTSettingFormFields(payload);
	};

	// useEffect(() => {
	// 	if (id && selectedDataRecord) {
	// 		Object.keys(selectedDataRecord).forEach((fieldName) => {
	// 			const fieldValue = selectedDataRecord[fieldName];
	// 			formMethods.setValue(fieldName, fieldValue ?? undefined);
	// 		});
	// 	}
	// }, [id, selectedDataRecord]);

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const { isPending: isDeleting, mutate: deleteBCEHTSettingById } = useMutation(
		{
			mutationFn: BCEHTSettingService.delBCEHTSetting,

			onSuccess: (res: string | IServerError | IResponse) => {
				if (typeof res === "string") {
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
				} else {
					setSnackbarData({
						open: true,
						message: "Something went wrong, try again later!",
						severity: "error",
					});
				}
				formMethods.reset();
				scrollPanelFieldsToTop();
				let path = `/bc-eht-setting/`;
				navigate(path);
				return queryClient.invalidateQueries({
					queryKey: [BCEHTSetting_LIST_DATA_QUERY_KEY],
				});
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		}
	);

	const deleteBCEHTSettingField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let delPayload = {
				bCEHTSettingId: bCEHTSettingId,
				navigateToLogout: navigateToLogout,
			};
			deleteBCEHTSettingById(delPayload);
			setDialogOpen(false);
		}
	};

	// const onDeleteClicked = () => {
	// 	setDialogOpen(true);
	// 	setSingleDelete(true);
	// 	if (selectedDataRecord) {
	// 		setBCEHTSettingId(selectedDataRecord.id);
	// 	}
	// };

	const onCloseHandler = () => {
		setDialogOpen(false);
	};
	const onCloseArchiveHandler = () => {
		setArchiveDialogOpen(false);
	};

	const onResetClicked = () => {
		setSnackbarData({
			open: true,
			message: "Success",
			severity: "success",
		});
		formMethods.reset();
		scrollPanelFieldsToTop();
		let path = `/bc-eht-setting/`;
		navigate(path);
	};

	const onYearSelectionChangeHandler = async (selectedYear: any) => {
		let value = selectedYear.value;
		(async () => {
			try {
				setIsPending(true);
				const responce = await BCEHTSettingService.getById(
					value,
					navigateToLogout
				);
				Object.keys(responce).forEach((fieldName) => {
					const fieldValue = responce[fieldName];
					formMethods.setValue(fieldName, fieldValue ?? undefined);
				});
				if (responce.message === "Not Found") {
					formMethods.reset();
					formMethods.setValue("taxYear", value);
					let path = `/bc-eht-setting/`;
					navigate(path);
				} else if (responce && responce.id) {
					let path = `/bc-eht-setting/${responce.id}`;
					navigate(path);
				}
			} catch {

				setIsPending(false);
			} finally {
				setIsPending(false);
			}
		})();
	};

	const expandedLabels = ["Misc.", "BC EHT Settings"];
	const expandedStatutoryHolidaySetupOrSetupItem = "BC EHT Settings";
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Misc." &&
				item.label === expandedStatutoryHolidaySetupOrSetupItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedStatutoryHolidaySetupOrSetupItem && "/bc-eht-setting",
			};
			return enhancedItem;
		});
	};

	useEffect(() => {
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	return (
		<Grid
			sx={{
				pb: "5rem",
				"& .MuiAppBar-root": {
					zIndex: 2,
					top: subMenuVisibility ? "154px" : "112px",
				},
			}}
		>
			<BackdropCircularProgress
				open={isSubmitting || isPending || isDeleting || isPendingYearsList}
				testId="comapny Setup"
			/>
			<DeleteConfirmationDialog
				open={openDialog}
				deleteActionHandler={deleteBCEHTSettingField}
				cancelActionHandler={onCloseHandler}
				testId="bc-eht-setting-confirmation-dialog"
			/>

			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<Grid size={{ xs: 12, sm: 12, md: 3.5, lg: 3.5, xl: 3.5 }}>
							<SingleColumnLayout
								children={
									<StandardPanel
										title="BC EHT Settings"
										testId={`bc-eht-settings`}
										// leftSpacing={1}
										// rightSpacing={1}
										showTitle={false}
										variant="outlined"
										topPadding={2}
									>
										<BCEHTSettingInfoSetupFieldsPanel
											yearsDDL={yearsDDLList ? yearsDDLItems : []}
											onYearSelectionChangeHandler={
												onYearSelectionChangeHandler
											}
										/>
									</StandardPanel>
								}
								toolbar={
									<BCEHTSettingSetupToolbar
										onResetClicked={onResetClicked}
										// onDeleteClicked={onDeleteClicked}
										isSubmitting={false}
										id={id}
									/>
								}
								showFullDiaplay={true}
								toolbarPosition="bottom"
								testId="form-layout"
							/>
						</Grid>
					</Grid>
				</form>
			</FormProvider>

			{/* Archive Dialogue */}
			<EnhancedDialog
				dialogTitle="Please Confirm"
				key="dialog-box"
				testId="dialog"
				onCloseHandler={onCloseArchiveHandler}
				open={openArchiveDialog}
				dialogContent={null}
				showdividers={false}
			/>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={"bc-eht-setting-snackbar"}
				open={snackbarData.open}
			/>
		</Grid>
	);
};
